import { Card, CheckboxField, Flex, Modal, Text, useToast } from "@wayflyer/flyui";
import * as React from "react";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import type { BankAccount, BankConnection } from "../bank-connection-display";
import styles from "./bank-accounts-filter-modal.module.css";

function buildFilterStateFromConnections(connections: BankConnection[]): FilterBankAccountsParams {
  const connectionsMap: FilterBankAccountsParams = { connections: {} };
  for (const connection of connections) {
    connectionsMap.connections[connection.id] = { accounts: {} };
    const accounts = connection.accounts || [];
    for (const account of accounts) {
      connectionsMap.connections[connection.id].accounts[account.id] = {
        enabled: connection.accountsSelected ? account.pull_enabled : true,
      };
    }
  }
  return connectionsMap;
}

export interface FilterBankAccountsParams {
  connections: {
    [connectionId: string]: {
      accounts: {
        [accountId: string]: { enabled: boolean };
      };
    };
  };
}

export interface BankAccountsFilterModalProps {
  bankConnections: BankConnection[];
  onConfirm: (accountFilterState: FilterBankAccountsParams) => Promise<void>;
  isOpen: boolean;
  close: () => void;
  /** Called when the modal is dismissed without confirming (X / Escape / backdrop). */
  onCancel?: () => void;
}
/**
 * BankAccountsFilterModal component
 */
export const BankAccountsFilterModal = ({
  onConfirm,
  bankConnections,
  isOpen,
  close,
  onCancel,
}: BankAccountsFilterModalProps): UIElement => {
  const intl = useIntl();
  const toaster = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  const [accountFilterState, setAccountFilterState] = useState<FilterBankAccountsParams>(
    buildFilterStateFromConnections(bankConnections)
  );

  const onAccountSelect = useCallback(
    (connection: BankConnection, account: BankAccount, selected: boolean) => {
      const newState: FilterBankAccountsParams = {
        connections: {
          ...accountFilterState.connections,
          [connection.id]: {
            accounts: {
              ...accountFilterState.connections[connection.id].accounts,
              [account.id]: { enabled: selected },
            },
          },
        },
      };
      setAccountFilterState(newState);
    },
    [accountFilterState]
  );

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm(accountFilterState);
      close();
    } catch (error) {
      toaster.critical(error as string);
    } finally {
      setIsLoading(false);
      close();
    }
  }, [onConfirm, accountFilterState, close, toaster]);

  const renderAccountSelect = (connection: BankConnection, account: BankAccount) => {
    const checked = accountFilterState.connections[connection.id].accounts[account.id].enabled;
    return (
      <Card key={account.id}>
        <CheckboxField
          name={`select-account-${account.id}-field`}
          options={{
            name: `select-account-${account.id}`,
            label: `${account.name} - ${account.type} - ******${account.mask}`,
            checked,
            onChange: (checked) => {
              onAccountSelect(connection, account, checked);
            },
          }}
        ></CheckboxField>
      </Card>
    );
  };

  const renderSelectionMenuForConnection = (connection: BankConnection) => {
    return (
      <div key={connection.id}>
        <Text fullWidth className={styles.heading}>
          {connection.institutionName}
        </Text>
        <Flex gap="4" direction="column">
          {connection.accounts?.map((account) => renderAccountSelect(connection, account))}
        </Flex>
      </div>
    );
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: "bank accounts filter modal - title",
        defaultMessage: "Select Accounts",
      })}
      primaryAction={{
        content: intl.formatMessage({
          id: "bank accounts filter: Continue",
          defaultMessage: "Continue",
        }),
        onAction: handleConfirm,
        loading: isLoading,
        analyticsId: "bank-accounts-filter-continue",
      }}
      isOpen={isOpen}
      onClose={() => {
        close();
        onCancel?.();
      }}
    >
      <div>
        <Text>
          {intl.formatMessage({
            id: "bank accounts filter modal - body",
            defaultMessage: "Please select all accounts for your business. We only pull data for selected accounts.",
          })}
        </Text>
        <Flex direction="column" gap="5">
          {bankConnections.map((connection) => renderSelectionMenuForConnection(connection))}
        </Flex>
      </div>
    </Modal>
  );
};
